import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { STALE } from '@/lib/constants'

// ── PSGC gazetteer place search (migration 0025) ──────────────────────────────
//
// Backs the destination box typeahead with the real PH location hierarchy, so
// any barangay / city / municipality / province is pickable — independent of
// whether we currently have inventory there (that's suggest_destinations' job).

export type PlaceLevel = 'region' | 'province' | 'city' | 'municipality' | 'barangay'

export interface PlaceSuggestion {
  code:          string
  name:          string
  level:         PlaceLevel
  region_name:   string | null
  province_name: string | null
  city_name:     string | null
  /** Display string, e.g. "Mactan, Lapu-Lapu City, Cebu". */
  path_label:    string
  relevance:     number
}

/** Short type badge for a place level, shown Booking-style on the dropdown row. */
export function placeLevelLabel(level: PlaceLevel): string {
  switch (level) {
    case 'region':       return 'Region'
    case 'province':     return 'Province'
    case 'city':         return 'City'
    case 'municipality': return 'Municipality'
    case 'barangay':     return 'Area'
  }
}

/**
 * Typeahead matches from the PSGC gazetteer. Empty query → no rows (the caller
 * falls back to inventory "trending"). Gate with `enabled` so it only fires
 * while the dropdown is open and there's a term to match.
 */
export function usePlaceSuggestions(query: string, enabled = true) {
  const term = query.trim()
  return useQuery<PlaceSuggestion[]>({
    queryKey: ['places', term.toLowerCase()],
    enabled: enabled && term.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_places', {
        p_query: term,
        p_limit: 8,
      })
      if (error) throw error
      return (data as PlaceSuggestion[]) ?? []
    },
    staleTime: STALE.catalog,
  })
}

/**
 * Resolve a destination string to its best gazetteer match, used to render the
 * Region › Province › City breadcrumb on the results page. When `province` is
 * given it disambiguates same-named places (e.g. Mactan in Cebu vs Masbate) by
 * preferring the match in that province. Returns null while loading / no match.
 */
export function useResolvePlace(
  destination: string | null | undefined,
  province?: string | null,
) {
  const term = (destination ?? '').trim()
  const prov = (province ?? '').trim().toLowerCase()
  return useQuery<PlaceSuggestion | null>({
    queryKey: ['place-path', term.toLowerCase(), prov],
    enabled: term.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_places', {
        p_query: term,
        p_limit: 8,
      })
      if (error) throw error
      const rows = (data as PlaceSuggestion[]) ?? []
      if (prov) {
        const match = rows.find((r) => (r.province_name ?? '').toLowerCase() === prov)
        if (match) return match
      }
      return rows[0] ?? null
    },
    staleTime: STALE.catalog,
  })
}
